import {
  IListElement,
  IListColumn,
  IListElementCell,
  IFormDocument,
  IValue,
  IIfStatement,
  IElseStatement,
} from 'app/services/api5-service/api.interface';

import { ISPFormState } from 'common/dynamic-form/model';
import { Conditional } from 'common/dynamic-form/services/condition.service';
import { getFontSize } from 'common/dynamic-form/utils/get-font-size';
import { getFontWeight } from 'common/dynamic-form/utils/get-font-weight';
import { DocHelper } from 'utils/dochelper';

import { IListCellUi, ListCellCollapseUi } from '../../list-cell/model';

/**
 * Append cell hint, if existed
 *
 * @param cell - FE cell base meta
 * @param element - BE element meta
 * @param column - BE column meta
 */
function appendCellHint(
  cell: IListCellUi,
  element: IListElement,
  column: IListColumn,
): void {
  if (column.$hint === 'yes') {
    const hintValue = element[`${cell.columnName}_hint`] as IValue;
    cell.hint = hintValue?.$;
  }
}

/**
 * Get collapse object for card term cell
 *
 * @param column - be data for column
 * @param doc
 */
function getCollapseForCell(
  column: IListColumn,
  doc: IFormDocument,
): ListCellCollapseUi | undefined {
  return column.$collapsible === 'yes'
    ? {
        collapseMsg: DocHelper.getMessage('collapse', doc),
        expandMsg: DocHelper.getMessage('expand', doc),
      }
    : undefined;
}

/**
 * Get cell id. Combination of column name and row id
 *
 * @param params
 * @param params.columnName
 * @param params.rowId
 */
function getCellId(params: { columnName: string; rowId?: string }): string {
  return `${params.columnName}_${params.rowId || 'any'}`;
}

/**
 * Get if/else conditions statement for rows scope
 *
 * @param conditions
 * @param rowId
 */
function getRowScopedStatement<T extends IElseStatement | IIfStatement>(
  conditions: T | T[],
  rowId?: string,
): T[] {
  return DocHelper.getArrayWrapperValue(conditions).map(statement => ({
    ...statement,
    $hide: statement.$hide
      ? getCellId({
          columnName: statement.$hide,
          rowId,
        })
      : undefined,
  }));
}

/**
 * Get cell hidden-conditions scoped metadata
 *
 * @param column
 * @param rowId
 */
function getCellConditional(
  column: IListColumn,
  rowId?: string,
): Conditional | undefined {
  if (!column.if && !column.else) {
    return undefined;
  }

  return {
    $name: getCellId({
      columnName: column.$name,
      rowId,
    }),
    if: column.if ? getRowScopedStatement(column.if, rowId) : undefined,
    else: column.else ? getRowScopedStatement(column.else, rowId) : undefined,
  };
}

/**
 * Get base cell FE metadata
 *
 * @param element - list element
 * @param column
 * @param state
 * @param cellData - BE data for cell
 * @param rowId
 */
export function getBaseCell(
  element: IListElement,
  column: IListColumn,
  state: ISPFormState,
  cellData?: IListElementCell,
  rowId?: string,
): IListCellUi {
  const id = getCellId({
    columnName: column.$name,
    rowId,
  });

  const cell: IListCellUi = {
    id,
    columnName: column.$name,
    horizontalAlign: column.$align || 'left',
    verticalAlign: column.$vertical_align,
    type: column.$type,
    width: column.$width,
    height: column.$height,
    ellipsis: column.$ellipsis === 'yes',
    collapse: getCollapseForCell(column, state.docSubject.value),
    columnTitle: DocHelper.getMessage(
      `list_${column.$name}`,
      state.docSubject.value,
    ),
    fullwidth: cellData?.$fullwidth === 'yes',
    colspan: 1,
    color: cellData?.$color || element.$color || column.$color,
    size: getFontSize(cellData?.$size || element.$size || column.$size),
    weight: getFontWeight(
      cellData?.$weight || element.$weight || column.$weight,
    ),
    isHidden$: state.conditionService.isHidden$(id),
    conditional: getCellConditional(column, rowId),
  };
  appendCellHint(cell, element, column);

  if (cell.conditional) {
    // set initial undefined value in order to trigger condition system. Actual value will be set later, or won't be setted at all
    state.conditionService.handleConditions(cell.conditional, undefined);
  }

  return cell;
}
